import { z } from "zod";
import {
	batchSourceSchema,
	batchStatusSchema,
	dietSchema,
	orderItemStatusSchema,
	orderStatusSchema,
	serviceRequestStatusSchema,
	serviceRequestTypeSchema,
} from "./enums";
import { lineOfferSchema, quoteOffersSchema } from "./offers";

/**
 * Ordering shapes shared by diners and staff: order lines, cart quotes,
 * placing a batch, the diner's status page and service requests. See
 * docs/architecture/api.md, Ordering.
 */

const uniqueIds = (ids: string[]) => new Set(ids).size === ids.length;

/** A note on a line or a batch. An empty string is no note. */
export const optionalNote = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.transform((v) => (v === "" ? null : v))
		.nullable()
		.optional();

/** One line as a client sends it: the item, its choices and how many. */
export const orderLineInputSchema = z.object({
	itemId: z.uuid(),
	/** Required when the item has sizes, null otherwise. */
	variantId: z.uuid().nullable().default(null),
	modifierOptionIds: z.array(z.uuid()).max(30).refine(uniqueIds, "Options must differ").default([]),
	quantity: z.number().int().min(1).max(50),
	note: optionalNote(140),
	/**
	 * How the diner added it (P9-T19): from the menu, a suggestion or "Order
	 * again". Staff batches are always `staff`, whatever is sent.
	 */
	source: z.enum(["menu", "suggestion", "reorder"]).default("menu"),
});
export type OrderLineInput = z.input<typeof orderLineInputSchema>;

/** `public.ordering.getCartQuote` */
export const cartQuoteInputSchema = z.object({
	lines: z.array(orderLineInputSchema).min(1).max(50),
	/** A coupon the diner entered (`public.promotions.applyCoupon`). */
	couponCode: z.string().trim().max(40).nullable().optional(),
});
export type CartQuoteInput = z.input<typeof cartQuoteInputSchema>;

/**
 * Why a line cannot be ordered. `not_found`: deleted or hidden since the menu
 * was loaded. `unavailable`: the item, its size or an option is sold out, or
 * its category is outside its hours. `invalid_choice`: a size or option that
 * does not belong to the item, or too few or too many options in a group.
 */
export const LINE_PROBLEMS = ["not_found", "unavailable", "invalid_choice"] as const;
export const lineProblemSchema = z.enum(LINE_PROBLEMS);
export type LineProblem = z.infer<typeof lineProblemSchema>;

export const quoteLineSchema = z.object({
	/** Position in the request. */
	index: z.number().int(),
	itemId: z.uuid(),
	variantId: z.uuid().nullable(),
	modifierOptionIds: z.array(z.uuid()),
	quantity: z.number().int(),
	note: z.string().nullable(),
	/** Null when the item is not found. */
	name: z.string().nullable(),
	variantName: z.string().nullable(),
	modifiers: z.array(
		z.object({ id: z.uuid(), name: z.string(), priceDeltaPaise: z.number().int() }),
	),
	/** The size's price, or the item's. 0 when not found. */
	unitPricePaise: z.number().int(),
	/** For one unit. */
	modifiersTotalPaise: z.number().int(),
	/** (unit + modifiers) × quantity, before the offer. 0 for a line with a problem. */
	lineTotalPaise: z.number().int(),
	/** The item offer that applies now: so much off each unit. */
	offer: lineOfferSchema.nullable(),
	problem: lineProblemSchema.nullable(),
});
export type QuoteLine = z.infer<typeof quoteLineSchema>;

export const taxRowSchema = z.object({
	rateBp: z.number().int(),
	taxablePaise: z.number().int(),
	cgstPaise: z.number().int(),
	sgstPaise: z.number().int(),
});

/** Totals from `calculateBill` in billing-core, as the bill would show them. */
export const orderTotalsSchema = z.object({
	subtotalPaise: z.number().int(),
	discountPaise: z.number().int(),
	serviceChargePaise: z.number().int(),
	taxablePaise: z.number().int(),
	cgstPaise: z.number().int(),
	sgstPaise: z.number().int(),
	roundOffPaise: z.number().int(),
	totalPaise: z.number().int(),
	/** One row per tax rate. Empty unless the GST mode is regular. */
	taxes: z.array(taxRowSchema),
	pricesIncludeTax: z.boolean(),
});
export type OrderTotals = z.infer<typeof orderTotalsSchema>;

/**
 * The cart priced by the server, with the running total including taxes
 * (SF-06). Lines with a problem are left out of the totals. `ok` is true when
 * no line has a problem.
 */
export const cartQuoteSchema = z.object({
	lines: z.array(quoteLineSchema),
	/** With the item offers and the cart's bill offer or coupon taken off. */
	totals: orderTotalsSchema,
	/** The bill offer, the nudge towards the nearest one and the coupon's state (SF-13). */
	offers: quoteOffersSchema,
	ok: z.boolean(),
});
export type CartQuote = z.infer<typeof cartQuoteSchema>;

/** Indian mobile number, kept as 10 digits: "+91 98640 12345" → "9864012345". */
export const guestPhoneSchema = z
	.string()
	.transform((v) => v.replace(/[\s-]/g, "").replace(/^(\+91|0)/, ""))
	.pipe(z.string().regex(/^[6-9]\d{9}$/, "A 10 digit mobile number"));

/** `public.ordering.placeBatch` */
export const placeBatchInputSchema = z.object({
	/** Made by the client once per "Place order"; a retry sends the same one. */
	clientRequestId: z.uuid(),
	lines: z
		.array(
			orderLineInputSchema.extend({
				/**
				 * The unit price with options that the diner saw, after the item
				 * offer: the quote line's `unitPricePaise + modifiersTotalPaise -
				 * offer.unitDiscountPaise`. When the server's differs (a price
				 * changed, or an offer started or ended): `PRICE_CHANGED`.
				 */
				expectedUnitPricePaise: z.number().int().min(0),
			}),
		)
		.min(1)
		.max(50),
	note: optionalNote(200),
	/** Optional, never required for dine-in (SF-16). Kept on the guest session. */
	guestName: optionalNote(60),
	guestPhone: guestPhoneSchema.nullable().optional(),
	/** A coupon from the cart. Kept for the bill when it can be used now. */
	couponCode: z.string().trim().max(40).nullable().optional(),
});
export type PlaceBatchInput = z.input<typeof placeBatchInputSchema>;

// ---------------------------------------------------------------------------
// What diners see.
// ---------------------------------------------------------------------------

export const guestOrderItemSchema = z.object({
	id: z.uuid(),
	/** The menu item and the choices, for "Order again" (P9-T17). */
	itemId: z.uuid().nullable(),
	variantId: z.uuid().nullable(),
	modifierOptionIds: z.array(z.uuid()),
	name: z.string(),
	variantName: z.string().nullable(),
	diet: dietSchema,
	modifiers: z.array(z.object({ name: z.string(), priceDeltaPaise: z.number().int() })),
	unitPricePaise: z.number().int(),
	modifiersTotalPaise: z.number().int(),
	quantity: z.number().int(),
	note: z.string().nullable(),
	/** `removed`: staff took it out when accepting (sold out). `cancelled`: cancelled later. */
	status: orderItemStatusSchema,
});
export type GuestOrderItem = z.infer<typeof guestOrderItemSchema>;

/** A batch as the diner's status page shows it. */
export const guestBatchSchema = z.object({
	id: z.uuid(),
	status: batchStatusSchema,
	source: batchSourceSchema,
	createdAt: z.iso.datetime(),
	decidedAt: z.iso.datetime().nullable(),
	/** Shown to the diner when rejected. */
	rejectReason: z.string().nullable(),
	note: z.string().nullable(),
	/** Placed from this phone. */
	mine: z.boolean(),
	items: z.array(guestOrderItemSchema),
});
export type GuestBatch = z.infer<typeof guestBatchSchema>;

export const placeBatchResultSchema = z.object({ batch: guestBatchSchema });
export type PlaceBatchResult = z.infer<typeof placeBatchResultSchema>;

// ---------------------------------------------------------------------------
// Service requests (SF-10).
// ---------------------------------------------------------------------------

/** What diners can ask for. `other` exists in the data but is not offered. */
export const GUEST_SERVICE_REQUEST_TYPES = ["waiter", "water", "bill"] as const;

export const createServiceRequestInputSchema = z.object({
	type: z.enum(GUEST_SERVICE_REQUEST_TYPES),
});

export const guestServiceRequestSchema = z.object({
	id: z.uuid(),
	type: serviceRequestTypeSchema,
	status: serviceRequestStatusSchema,
	createdAt: z.iso.datetime(),
	lastRequestedAt: z.iso.datetime(),
});
export type GuestServiceRequest = z.infer<typeof guestServiceRequestSchema>;

/** `merged`: a repeat tap within 60 seconds, joined to the request already sent. */
export const createServiceRequestResultSchema = z.object({
	request: guestServiceRequestSchema,
	merged: z.boolean(),
});
export type CreateServiceRequestResult = z.infer<typeof createServiceRequestResultSchema>;

/** `public.ordering.getMyOrder`: the diner's status page. */
export const myOrderSchema = z.object({
	table: z.object({ id: z.uuid(), label: z.string() }),
	/** The table's open or billed order, if any. */
	order: z
		.object({
			id: z.uuid(),
			orderNo: z.number().int(),
			status: orderStatusSchema,
			openedAt: z.iso.datetime(),
		})
		.nullable(),
	/**
	 * The order's batches, the table's pending batches, and this diner's
	 * rejected ones, oldest first.
	 */
	batches: z.array(guestBatchSchema),
	/** The table's requests that are not resolved. */
	serviceRequests: z.array(guestServiceRequestSchema),
	/** Staff blocked this session: ordering and calls are refused. */
	blocked: z.boolean(),
});
export type MyOrder = z.infer<typeof myOrderSchema>;
